/**
 * Launcher V2 PR B U3 — React Query access to U2's matched-member endpoints.
 *
 * Exists so `MembersPanel` (the persistent panel, scoped by a rung's Preview)
 * and `ReviewStep` (the Routed / No-sequence member tallies) ask for the ladder
 * with the SAME query key: React Query then serves both from one fetch, which
 * is what keeps "the panel and the Review counts agree" true by construction
 * rather than by two components happening to call the same endpoint.
 *
 * The keys sit under `['playbooks', …]` like every other v2 read, so
 * `ReviewStep`'s "Re-check" (`invalidateQueries({ queryKey: ['playbooks'] })`)
 * already refreshes them — no new invalidation call site.
 *
 * No component logic here and no zod: the wrappers in `api-client.ts` already
 * parse; this file only wires them to the cache.
 */

import { useQuery } from '@tanstack/react-query';

import { useApi } from '../_shared/hooks';
import type { FilterGroup, GroupLogic } from '../_shared/flow/filter';

import { getMatchedMembersLadder, matchMembersByFilter } from './api-client';
import { matchedMembersPrefixKey } from './query-keys';
import type { AudienceRuleDraft } from './rung-scope-store';

/**
 * Play isolation (plan 2026-09-25-002, U5) — the optional `playId` appends
 * `{ playId }` (same suffix rule `query-keys.ts`'s scoped keys use),
 * so the play's own ladder never collides with the template's cache entry.
 * Built on `matchedMembersPrefixKey()` (plan 2026-09-27-006 U6) so the broad
 * `matchedMembersPrefixKey()` invalidation every write path already calls
 * still reaches this key.
 */
export function matchedMembersLadderKey(campaignSfdcId: string, playbookId: string | null, playId?: string) {
  return playId === undefined
    ? ([...matchedMembersPrefixKey(), 'ladder', campaignSfdcId, playbookId] as const)
    : ([...matchedMembersPrefixKey(), 'ladder', campaignSfdcId, playbookId, { playId }] as const);
}

/**
 * The whole first-match-wins pass for one campaign × Playbook.
 *
 * `playbookId === null` (no rung scoped, or a Play with no Playbook) leaves the
 * query disabled — the panel renders the full roster in that case and must not
 * spend a request working out a ladder nobody is looking at.
 *
 * Play isolation (plan 2026-09-25-002, U5) — the optional `playId` buckets by
 * that play's own ladder (Decision 12); `undefined` in the playbook doorway
 * keeps today's template read.
 */
export function useMatchedMembersLadder(campaignSfdcId: string, playbookId: string | null, playId?: string) {
  const { apiFetch } = useApi();

  return useQuery({
    queryKey: matchedMembersLadderKey(campaignSfdcId, playbookId, playId),
    queryFn: () => getMatchedMembersLadder(apiFetch, { campaignSfdcId, playbookId: playbookId!, playId }),
    enabled: !!campaignSfdcId && !!playbookId,
    // Codex hardening fix: this app's global default is `staleTime: 0`
    // (`web/app-home/src/main.tsx`), and `PlayFlowShell` mounts a fresh
    // `MembersPanel` per step (see its own doc comment) — every tab switch
    // and every Preview click was a component remount subscribing to a
    // query React Query considered instantly stale, so it refetched and
    // visibly flashed even though nothing had changed. Every write path that
    // can move this ladder (`QualifyStep`'s `handleReorder`, on both its
    // success and catch branches; `ReviewStep`'s "Re-check") already
    // explicitly invalidates this key or the broad `['playbooks']` prefix
    // that covers it, so an additional numeric TTL is redundant — the same
    // "explicit invalidation, not a TTL" shape `useViewArtifacts`/`useArtifact`
    // use for SSE-controlled freshness (there the trigger is SSE; here it's
    // these invalidation calls).
    staleTime: Infinity,
  });
}

/**
 * Query key for any ad hoc filter match — the Audience Rule's staged draft OR
 * a rung's unsaved rung-draft conditions (U6, R-Q1). Both scopes end up here so
 * they share ONE cache entry per (campaign, filter) pair — a rung-draft preview
 * and an Audience Rule preview built from the identical groups/groupLogic never
 * issue two requests for the same answer.
 *
 * The filter is serialized INTO the key so a new "Apply filters" click is a new
 * query rather than a stale hit — cheap because `FilterPopover` only emits
 * `onFiltersChange` on Apply (`web/app-home/src/components/flow/filters/
 * FilterPopover.tsx` — its internal draft never leaks per keystroke), so this
 * is one request per apply, not one per edit.
 */
export function filterMatchedMembersKey(campaignSfdcId: string, groups: FilterGroup[], groupLogic: GroupLogic) {
  const filterKey = JSON.stringify({ groups, groupLogic });
  return [...matchedMembersPrefixKey(), 'filter', campaignSfdcId, filterKey] as const;
}

/**
 * Any ad hoc filter, previewed against real members: the Audience Rule's
 * UNSAVED draft (plan Decision 3) and a rung's UNSAVED rung-draft conditions
 * (U6, R-Q1) are the same query shape, generalized here so both call sites and
 * both `MembersPanel`/`RungRow` consumers share one hook, one cache-key shape,
 * and can never quietly diverge on what "matching members for this filter"
 * means.
 */
export function useFilterMatchedMembers(
  campaignSfdcId: string,
  groups: FilterGroup[],
  groupLogic: GroupLogic,
  enabled: boolean,
) {
  const { apiFetch } = useApi();

  return useQuery({
    queryKey: filterMatchedMembersKey(campaignSfdcId, groups, groupLogic),
    queryFn: () => matchMembersByFilter(apiFetch, { campaignSfdcId, groups, groupLogic }),
    enabled: enabled && !!campaignSfdcId,
    // Codex hardening fix — same reasoning as `useMatchedMembersLadder` above:
    // the app's global `staleTime: 0` plus `MembersPanel` remounting on every
    // tab switch/Preview click caused a visible refetch flash. The filter is
    // already IN the query key (a new "Apply filters" click is a new key, not
    // a stale hit on this one), and `ReviewStep`'s "Re-check" invalidates the
    // broad `['playbooks']` prefix that covers this key too, so a TTL of 0
    // buys nothing but the flash.
    staleTime: Infinity,
  });
}

/**
 * `useFilterMatchedMembers` specialized to the Audience Rule's draft shape —
 * kept as the call site `AudienceRuleCard`/`MembersPanel` already use so
 * neither needs to destructure `draft.groups`/`draft.groupLogic` itself.
 */
export function useAudienceMatchedMembers(
  campaignSfdcId: string,
  draft: AudienceRuleDraft,
  enabled: boolean,
) {
  return useFilterMatchedMembers(campaignSfdcId, draft.groups, draft.groupLogic, enabled);
}
